import React, { useCallback, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOnboardingFlow } from '../../../store/onboarding/useOnboardingFlow'
import { useOnboardingActions } from '../../../store/onboarding/useOnboardingActions'
import { useOnboardingCapabilities } from '../../../store/onboarding/useOnboardingCapabilities'
import {
  type CapabilityState,
  DefaultOnboardingFlowStepId,
  Capability,
  OnboardingFlowStatus,
  type OnboardingStep,
  OnboardingStepStatus,
} from '../../../models/Onboarding'
import { type IntegrationServiceId } from '../../../common/integrations'
import { ROUTER_BASEPATH } from '../../../common/constants'
import styles from './OnboardingPage.module.scss'
import { OnboardingIntegrationStep } from './OnboardingIntegrationStep/OnboardingIntegrationStep'
import { OnboardingStepWrapper } from './OnboardingStepWrapper/OnboardingStepWrapper'
import { OnboardingStepFinished } from './OnboardingStepFinished/OnboardingStepFinished'
import { OnboardingStepHeader } from './OnboardingStepHeader/OnboardingStepHeader'
import { Button } from '../../../components/Button/Button'
import { getIntegrationSpecificCompletedStepTitle, getCustomCompletedLink, ONBOARDING_STEP_COPIES, type OnboardingStepCopies } from '../copies/onboardingCopies'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking/hooks'
import { GrowthTrackEvent } from '../../../apps/professional-app/fa/common/tracking'
import { CAPABILITY_TO_GROWTH_EVENT } from '../tracking_utils'

const STEP_ANCHOR_PREFIX = 'step-'

interface OnboardingStepCardProps {
  step: OnboardingStep
  stepConfig: OnboardingStepCopies
  capabilityState: CapabilityState | undefined
  buildPostConnectPath: (serviceId: IntegrationServiceId) => string
  onSkip: () => void
  isSkipping: boolean
  onIntegrationSelect?: (serviceId: IntegrationServiceId) => void
  isLast?: boolean
  id?: string
}

const OnboardingStepCard: React.FC<OnboardingStepCardProps> = ({
  step,
  stepConfig,
  capabilityState,
  buildPostConnectPath,
  onSkip,
  isSkipping,
  onIntegrationSelect,
  isLast,
  id,
}) => {
  const isCompleted = step.status === OnboardingStepStatus.COMPLETED

  const extraCompletedLinks = getCustomCompletedLink(capabilityState?.connectedIntegrations ?? [], stepConfig.capability)
  const completedLinks = extraCompletedLinks ? [...(stepConfig.completedLinks || []), extraCompletedLinks] : stepConfig.completedLinks
  const completedTitleOrDefault =
    getIntegrationSpecificCompletedStepTitle(capabilityState?.connectedIntegrations ?? []) || stepConfig.completedOnboardingListTitle

  const skipButton = (
    <button type='button' className='btn button-medium zocks-text-primary p-0' disabled={isSkipping} onClick={onSkip}>
      {isSkipping ? 'Saving…' : 'Set up later in integrations'}
    </button>
  )

  return (
    <OnboardingStepWrapper
      icon={stepConfig.icon}
      title={isCompleted ? stepConfig.completedOnboardingListTitle : stepConfig.pendingOnboardingListTitle}
      description={isCompleted ? stepConfig.completedOnboardingListDescription : stepConfig.pendingOnboardingListDescription}
      isCompleted={isCompleted}
      isSkipped={step.status === OnboardingStepStatus.SKIPPED}
      isLast={isLast}
      id={id}
    >
      <div className='pb-4 pt-4'>
        {isCompleted ? (
          <OnboardingStepFinished title={completedTitleOrDefault} bullets={stepConfig.completedBullets ?? []} links={completedLinks} showDecoration={false} />
        ) : (
          <OnboardingIntegrationStep
            icon={stepConfig.icon}
            headerTitle={stepConfig.pendingHeaderTitle}
            headerSubtitle={stepConfig.pendingHeaderSubtitle}
            serviceIds={capabilityState?.allowedServiceIds ?? []}
            selectorDisplayMode={'grid'}
            secondaryServiceIds={capabilityState?.allowedSecondaryIds}
            secondaryServiceIdsLabel={stepConfig.secondaryServiceIdsLabel}
            notificationText={stepConfig.onboardingStepNotification}
            connectedWithoutSync={capabilityState?.connectedWithoutSync}
            postConnectRedirectPath={buildPostConnectPath}
            secondaryAction={step.status !== OnboardingStepStatus.SKIPPED ? skipButton : null}
            capability={stepConfig.capability}
            onIntegrationSelect={onIntegrationSelect}
          />
        )}
      </div>
    </OnboardingStepWrapper>
  )
}

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { flow, isLoading } = useOnboardingFlow()
  const { areAllStepsCompleted, isDismissingFlow, isCompletingFlow, skippingStepIds, handleDismiss, handleComplete, handleSkipStep } =
    useOnboardingActions(flow)
  const capabilities = useOnboardingCapabilities()
  const { trackingClient } = useTracking()

  const activeStepId = location.hash.startsWith(`#${STEP_ANCHOR_PREFIX}`) ? location.hash.slice(STEP_ANCHOR_PREFIX.length + 1) : null

  const buildPostConnectPathForStep =
    (step: OnboardingStep, config: OnboardingStepCopies) =>
    (serviceId: IntegrationServiceId): string => {
      const anchor = `#${STEP_ANCHOR_PREFIX}${step.id}`
      const baseUrl = window.location.pathname + window.location.search
      const returnTo = encodeURIComponent(baseUrl + anchor)
      const intentMap: Partial<Record<Capability, string>> = {
        [Capability.CALENDAR_SOURCE]: 'calendar',
        [Capability.CONTACT_SOURCE]: 'contacts',
        [Capability.ZOOM]: 'zoom',
      }
      const intent = intentMap[config.capability]
      const intentParam = intent ? `&intent=${intent}` : ''
      return `${ROUTER_BASEPATH}/onboarding/post-connect/${serviceId}?returnTo=${returnTo}${intentParam}`
    }

  const isNotActive = !isLoading && flow?.status !== OnboardingFlowStatus.ACTIVE

  useEffect(() => {
    if (!isLoading && activeStepId) {
      document.getElementById(`${STEP_ANCHOR_PREFIX}${activeStepId}`)?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isLoading, activeStepId])

  useEffect(() => {
    if (isNotActive) {
      navigate('/workspace/upcoming', { replace: true })
    }
  }, [isNotActive, navigate])

  const stepsToDisplay = useMemo(() => (flow?.steps ?? []).filter((s) => s.status !== OnboardingStepStatus.NOT_ALLOWED), [flow])

  const handleIntegrationSelect = useCallback(
    (capability: Capability, serviceId: IntegrationServiceId) => {
      const eventName = CAPABILITY_TO_GROWTH_EVENT[capability]
      if (eventName) {
        trackingClient.track({ eventName, eventDetails: { integrationId: serviceId } })
      }
    },
    [trackingClient],
  )

  const handleFinishSetup = useCallback(() => {
    trackingClient.track({
      eventName: GrowthTrackEvent.ONBOARDING_FINISH_SETUP_CLICKED,
      eventDetails: { areAllStepsCompleted },
    })
    if (areAllStepsCompleted) {
      handleComplete()
    } else {
      handleDismiss()
    }
  }, [trackingClient, areAllStepsCompleted, handleComplete, handleDismiss])

  const sortedStepsWithConfigs = stepsToDisplay
    .map((step) => ({
      step,
      config: ONBOARDING_STEP_COPIES[step.id as DefaultOnboardingFlowStepId],
    }))
    .filter((a) => a.config)
    .sort((a, b) => a.config.onboardingScreenDisplayOrder - b.config.onboardingScreenDisplayOrder)

  if (isNotActive) {
    return null
  }

  return (
    <div className={classNames(styles.page, 'd-flex justify-content-center')}>
      <div className={classNames(styles.inner, 'd-flex flex-column align-items-center w-100')}>
        <OnboardingStepHeader
          icon={
            <span className='d-inline-flex align-items-center justify-content-center p-2 zocks-bg-color-primary rounded-circle'>
              <i className='bi bi-rocket-takeoff text-white fs-4' />
            </span>
          }
          title={areAllStepsCompleted && !isLoading ? 'You are all set!' : 'Welcome to Zocks!'}
          subtitle={
            areAllStepsCompleted && !isLoading
              ? 'Your AI Assistant is ready to join your first meeting and take notes automatically.'
              : "Let's make Zocks work for you. Setup takes a few minutes. After that, Zocks handles your meeting notes automatically."
          }
        />

        {isLoading ? (
          <div className='d-flex flex-column align-items-center gap-3 mt-5'>
            <Spinner />
            <div className='fw-semibold fs-4 mt-4'>Checking your progress</div>
            <div className='text-secondary'>Hold on for a second</div>
          </div>
        ) : (
          <>
            <div className='w-100 d-flex flex-column gap-2'>
              <OnboardingStepWrapper
                icon={<i className='bi bi-check-circle' />}
                title='Account created'
                description='Zocks is ready to start working for you'
                isCompleted
                isLast={stepsToDisplay.length === 0}
              >
                <OnboardingStepFinished
                  title='Account created successfully'
                  bullets={[
                    'Get 10+ hours a week back to focus on your clients',
                    'Automate your meeting notes, intake forms, client emails, and more',
                    'Get detailed reporting on your client conversations with insights',
                  ]}
                  links={[{ label: 'Setup your profile', to: '/account/profile-settings/profile-settings' }]}
                  showDecoration={false}
                />
              </OnboardingStepWrapper>
              {sortedStepsWithConfigs.map(({ step, config }, index) => {
                const anchorId = `${STEP_ANCHOR_PREFIX}${step.id}`
                return (
                  <OnboardingStepCard
                    key={step.id}
                    step={step}
                    stepConfig={config}
                    capabilityState={capabilities[config.capability]}
                    buildPostConnectPath={buildPostConnectPathForStep(step, config)}
                    onSkip={() => handleSkipStep(step.id)}
                    isSkipping={skippingStepIds.has(step.id)}
                    onIntegrationSelect={(serviceId) => handleIntegrationSelect(config.capability, serviceId)}
                    isLast={index === stepsToDisplay.length - 1}
                    id={anchorId}
                  />
                )
              })}
            </div>

            <div className='d-flex justify-content-center mt-2'>
              <Button
                variant={areAllStepsCompleted ? 'primary' : 'primaryGhost'}
                disabled={isDismissingFlow || isCompletingFlow}
                onClick={handleFinishSetup}
                loading={isDismissingFlow || isCompletingFlow}
              >
                Finish setup
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
